import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AlignVerticalJustifyStart,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Code2,
  Cog,
  Database,
  FileText,
  LayoutDashboard,
  Link2,
  Map,
  Settings,
  TableProperties,
  UserCog,
  Users,
  Users2Icon,
  X,
} from "lucide-react";

import { DRAWER_WIDTH, USER_ROLES } from "../utils/constants";
import { UserContext } from "../context/UserProvider";
import useAdminEventSettings from "../hooks/useAdminEventSettings";
import { isAdminRestrictedPath } from "../utils/accessControl";

const MINI_DRAWER_WIDTH = 76;
const MOBILE_DRAWER_WIDTH = 282;
const ICON_SIZE = 21;
const STROKE_WIDTH = 1.8;

const menuItems = [
  {
    text: "Dashboard",
    link: "/",
    icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#2f66ee",
  },
  {
    text: "OES Forms",
    link: "/oes-forms",
    icon: <TableProperties size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#7c3aed",
  },
  {
    text: "Registration Forms",
    link: "/form-links",
    icon: <FileText size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#0d8dcc",
  },
  {
    text: "Floor Plan",
    link: "/floor-plan-creator",
    icon: <Map size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#f97316",
  },
  {
    text: "Users List",
    icon: <Users2Icon size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#10a979",
    subcategories: [
      {
        text: "Exhibitor List",
        link: "/exhibitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        color: "#2f66ee",
      },
      {
        text: "Visitors List",
        link: "/visitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        color: "#10a979",
      },
      {
        text: "Trade Delegate Registrations",
        link: "/manage-delegates",
        icon: <UserCog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        color: "#f97316",
        roles: [USER_ROLES.SUPER],
      },
      {
        text: "Approved Trade Delegates",
        link: "/tradedelegates-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        color: "#7c3aed",
      },
      {
        text: "Service Codes",
        link: "/service-codes-list",
        icon: <Code2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        color: "#f59e0b",
      },
      {
        text: "Scanned Analytics",
        link: "/exhibitors-scanned-analytics",
        icon: (
          <AlignVerticalJustifyStart
            size={ICON_SIZE}
            strokeWidth={STROKE_WIDTH}
          />
        ),
        color: "#ef5da8",
      },
      // {
      //   text: "Print Staff Badge",
      //   link: "/print-staff-badge",
      //   icon: <Badge size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      //   color: "#0d8dcc",
      // },
    ],
  },
  {
    text: "Database",
    link: "/export-data",
    icon: <Database size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#4f46e5",
  },
  {
    text: "Settings",
    link: "/settings",
    icon: <Settings size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#64748b",
  },
  {
    text: "OES Explorer",
    link: "/oes-explorer",
    icon: <Cog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#a855f7",
    role: USER_ROLES.OES,
  },
  {
    text: "Onsite Module",
    link: "/onsite-links",
    icon: <Link2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    color: "#e8790b",
  },
];

const SideMenuBar = ({ open, setOpen }) => {
  const { user } = useContext(UserContext);
  const { data } = useAdminEventSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState({});
  const expanded = isMobile || open;

  const roleMenuItems = user?.role === USER_ROLES.TD_ADMIN
    ? [
        {
          text: "Manage TDs",
          link: "/manage-delegates",
          icon: <UserCog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
          color: "#2f66ee",
        },
      ]
    : menuItems.filter((item) => !item.role || item.role === user?.role);

  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isVisibleForRole = (item) =>
    !item.roles || item.roles.includes(user?.role);
  const isAllowedForAdmin = (item) =>
    !isAdmin || !item.link || !isAdminRestrictedPath(item.link);

  const visibleMenuItems = roleMenuItems
    .filter((item) => isVisibleForRole(item) && isAllowedForAdmin(item))
    .map((item) => ({
      ...item,
      subcategories: item.subcategories?.filter(
        (child) => isVisibleForRole(child) && isAllowedForAdmin(child)
      ),
    }))
    .filter(
      (item) => item.link || !item.subcategories || item.subcategories.length
    );

  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile, location.pathname, setOpen]);

  useEffect(() => {
    if (!open && !isMobile) setOpenSubmenu({});
  }, [isMobile, open]);

  const handleSubmenu = (label) => {
    if (!expanded) setOpen(true);
    setOpenSubmenu((previous) => ({ ...previous, [label]: !previous[label] }));
  };

  const renderMenuItems = (items, nested = false) =>
    items.map((item) => {
      const itemColor = item.color || "#2f66ee";
      const isActive = item.link
        ? location.pathname === item.link
        : item.subcategories?.some((child) => location.pathname === child.link);
      const submenuOpen =
        openSubmenu[item.text] || Boolean(isActive && item.subcategories);
      const componentProps = item.link
        ? item.link.startsWith("http")
          ? {
              component: "a",
              href: item.link,
              target: "_blank",
              rel: "noopener noreferrer",
            }
          : { component: RouterLink, to: item.link }
        : { component: "div" };

      const menuButton = (
        <ListItemButton
          {...componentProps}
          onClick={() => {
            if (item.subcategories) handleSubmenu(item.text);
            else if (isMobile) setOpen(false);
          }}
          sx={{
            minHeight: 48,
            mx: expanded ? 1.25 : 1,
            mb: 0.5,
            px: expanded ? 1.5 : 0,
            pl: nested && expanded ? 3 : expanded ? 1.5 : 0,
            justifyContent: expanded ? "initial" : "center",
            borderRadius: 2.25,
            color: isActive ? itemColor : "#536176",
            bgcolor: isActive ? alpha(itemColor, 0.09) : "transparent",
            position: "relative",
            "&::before":
              isActive && expanded
                ? {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 10,
                    bottom: 10,
                    width: 3,
                    borderRadius: "0 4px 4px 0",
                    bgcolor: itemColor,
                  }
                : {},
            "&:hover": {
              color: itemColor,
              bgcolor: alpha(itemColor, isActive ? 0.13 : 0.065),
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              width: 38,
              mr: expanded ? 1 : 0,
              justifyContent: "center",
              color: itemColor,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                bgcolor: alpha(itemColor, isActive ? 0.16 : 0.1),
                transition: "background-color 0.2s, transform 0.2s",
                ".MuiListItemButton-root:hover &": {
                  bgcolor: alpha(itemColor, 0.16),
                  transform: "scale(1.04)",
                },
              }}
            >
              {item.icon}
            </Box>
          </ListItemIcon>
          {expanded && (
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: nested ? 13.5 : 14,
                fontWeight: isActive ? 750 : 550,
                noWrap: true,
              }}
            />
          )}
          {item.subcategories &&
            expanded &&
            (submenuOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />)}
        </ListItemButton>
      );

      return (
        <React.Fragment key={item.text}>
          {!expanded ? (
            <Tooltip title={item.text} placement="right" arrow>
              {menuButton}
            </Tooltip>
          ) : (
            menuButton
          )}
          {item.subcategories && expanded && (
            <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderMenuItems(item.subcategories, true)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const drawerWidth = isMobile
    ? MOBILE_DRAWER_WIDTH
    : open
    ? DRAWER_WIDTH
    : MINI_DRAWER_WIDTH;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={() => setOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          overflowX: "hidden",
          bgcolor: "#ffffff",
          borderRight: "1px solid #e5eaf2",
          boxShadow: isMobile
            ? "12px 0 40px rgba(20,36,66,.15)"
            : "4px 0 18px rgba(20,36,66,.035)",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ minHeight: { xs: 64, md: 72 } }} />

        {expanded && (
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {isMobile && (
                <Typography
                  noWrap
                  sx={{
                    color: "#172033",
                    fontSize: 15,
                    fontWeight: 750,
                    mt: 0.3,
                  }}
                >
                  {data?.siteTitle || "ExpoPlanner"}
                </Typography>
              )}
            </Box>
            {isMobile && (
              <IconButton
                onClick={() => setOpen(false)}
                size="small"
                sx={{ bgcolor: "#f5f7fb", color: "#536176" }}
              >
                <X size={19} />
              </IconButton>
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            py: expanded ? 0.5 : 2,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#d8dee9",
              borderRadius: 5,
            },
          }}
        >
          <List disablePadding>{renderMenuItems(visibleMenuItems)}</List>
        </Box>

        {!isMobile && (
          <Box sx={{ p: 1.25 }}>
            <Divider sx={{ mb: 1.25 }} />
            <Tooltip
              title={open ? "Collapse menu" : "Expand menu"}
              placement="right"
              arrow
            >
              <ListItemButton
                onClick={() => setOpen(!open)}
                sx={{
                  minHeight: 46,
                  px: open ? 1.5 : 0,
                  justifyContent: open ? "initial" : "center",
                  borderRadius: 2.25,
                  color: "#667085",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    color: "#2f66ee",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    width: 38,
                    mr: open ? 1 : 0,
                    justifyContent: "center",
                    color: "inherit",
                  }}
                >
                  {open ? (
                    <ChevronLeft size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary="Collapse menu"
                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: 650 }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default SideMenuBar;
